import { sumBy } from 'lodash-es';
import dayjs from 'dayjs';
import { joinWithOxfordComma } from '@/modules/core/app/helpers/ArrayHelper';
import { BaseEntityModel } from '@/modules/core/app/models/abstracts/BaseEntityModel';
import { StripeInvoiceStatus } from '@/modules/billing/constants/StripeInvoiceStatus';
import { Currency } from '@/modules/predefined/models/Currency';
import { StripeInvoiceMetadata } from '@/modules/billing/models/stripe/StripeInvoiceMetadata';
import { StripeTax } from '@/modules/billing/models/stripe/StripeTax';
import { StripeCharge } from '@/modules/billing/models/stripe/StripeCharge';
import { StripePaymentIntent } from '@/modules/billing/models/stripe/StripePaymentIntent';
import { StripeInvoiceLineItem } from '@/modules/billing/models/stripe/StripeInvoiceLineItem';
import { StripeDiscountAmount } from '@/modules/billing/models/stripe/StripeDiscountAmount';
import { StripeCustomerAddress } from '@/modules/billing/models/stripe/StripeCustomerAddress';
import { StripeTransferData } from '@/modules/billing/models/stripe/StripeTransferData';
import { DateFormat } from '@/modules/core/app/constants/date.constants';
import NumeralService from '@/modules/core/app/services/NumeralService';
import { ColumnFormat } from '@/modules/core/app/constants/data.constants';
import { StripeProduct } from '@/modules/billing/models/stripe/StripeProduct';

export class StripeInvoice extends BaseEntityModel {
  public id: string;

  public customer: string;

  public customerName: string;

  public subtotalExcludingTax: number;

  public tax: number;

  public totalExcludingTax: number;

  public total: number;

  public number?: string;

  public status: StripeInvoiceStatus;

  public currency: Currency;

  public effectiveAt: number | null;

  public hostedInvoiceUrl?: string;

  public customerAddress?: Relationship<StripeCustomerAddress>;

  public paymentIntent?: Relationship<StripePaymentIntent>;

  public metadata: Relationship<StripeInvoiceMetadata>;

  public totalTaxAmounts?: Relationship<StripeTax[]>;

  public lines?: Relationship<StripeInvoiceLineItem[]>;

  public totalDiscountAmounts?: Relationship<StripeDiscountAmount[]>;

  public transferData?: Relationship<StripeTransferData>;

  public charge?: Relationship<StripeCharge> | string;

  constructor(model: Partial<StripeInvoice> = {}) {
    super(model);

    this.id = model.id as string;
    this.customer = model.customer as string;
    this.customerName = model.customerName as string;
    this.customerAddress = StripeCustomerAddress.mapModelOrNull(model.customerAddress) || undefined;
    this.status = model.status as StripeInvoiceStatus;
    this.subtotalExcludingTax = model.subtotalExcludingTax as number;
    this.effectiveAt = model.effectiveAt ?? null;
    this.tax = model.tax as number;
    this.totalExcludingTax = model.totalExcludingTax as number;
    this.total = model.total as number;
    this.number = model.number;
    this.currency = Currency.mapModel(model.currency);
    this.hostedInvoiceUrl = model.hostedInvoiceUrl;
    this.paymentIntent = StripePaymentIntent.mapModelOrNull(model.paymentIntent) || undefined;
    this.metadata = StripeInvoiceMetadata.mapModel(model.metadata);
    this.totalTaxAmounts = StripeTax.mapModelsOrNull(model.totalTaxAmounts) || undefined;
    this.lines = StripeInvoiceLineItem.mapModelsOrNull(model.lines) || undefined;
    this.totalDiscountAmounts =
      StripeDiscountAmount.mapModelsOrNull(model.totalDiscountAmounts) || undefined;
    this.transferData = StripeTransferData.mapModelOrNull(model.transferData) || undefined;
    this.charge =
      typeof model.charge === 'string'
        ? model.charge
        : StripeCharge.mapModelOrNull(model.charge as Partial<StripeCharge>) || undefined;
  }

  get fullPaymentDiscount(): StripeDiscountAmount | undefined {
    return this.totalDiscountAmounts?.find(
      (discountAmount: StripeDiscountAmount): boolean =>
        !!discountAmount.discount.coupon.metadata.isFullPaymentDiscount
    );
  }

  get fullPaymentDiscountValue(): number {
    return sumBy(this.totalDiscountAmounts, 'amount');
  }

  get formattedEffectiveAtDate(): string | null {
    return this.effectiveAt
      ? dayjs.unix(this.effectiveAt).format(DateFormat.LOCALIZED_DAY_MONTH_YEAR)
      : null;
  }

  get formattedTotal(): string {
    return NumeralService.formatValue(this.total / 100, ColumnFormat.FORMAT_CURRENCY, 2);
  }

  get displayProductNames(): string {
    const productNames: string[] = (this.lines as StripeInvoiceLineItem[]).map(
      // oxlint-disable-next-line no-unsafe-optional-chaining
      (line: StripeInvoiceLineItem) => (line.price?.product as StripeProduct).name
    );

    return joinWithOxfordComma(productNames);
  }
}
